import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import config from '../config.js';
import UserContext from '../components/common/UserContext.js';
import {GeneralContext} from '../components/FSARequestLicense/FSARequestLicenseContext.js';
// css
import '../assets/css/FSADetailedView.css';

// components
import PageHeader from '../components/common/PageHeader.js';
import Spinner from '../components/common/Spinner.js';
import SummaryChart from '../components/FSARequestLicense/SummaryChart.js';
import DetailedViewInfo from '../components/FSADetailedView/DetailedViewInfo.js';
import HostPanelBars from '../components/FSADetailedView/HostPanelBars.js';
import ProductPanelBars from '../components/FSADetailedView/ProductPanelBars.js';
import {useSupportContext} from "../components/common/SupportContext";

import Alert from '../components/common/Alert.js';
import {formatCurrency} from '../components/common/utilities.js';

// kendo react
import {Label} from "@progress/kendo-react-labels";
import {RadioGroup} from "@progress/kendo-react-inputs";
import {Switch} from "@progress/kendo-react-inputs";
import {orderBy} from '@progress/kendo-data-query';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {
    transportableLicensesSavedKey,
    groupByHostIdKey,
    groupByProductsKey,
    mainMessages,
    hostPendingSpecialKey,
    showExpiredLicensesKey,
    hostKey,
    genericErrorTitleKey,
    contactUsHeaderKey,
    flexibleSpendingAccountsKey,
    fsaLicenseViewKey,
} from '../assets/text/MultilingualText.js';
import {useHistory} from "react-router-dom";
import {convertDateFormat} from "../components/common/Grid";

const initialHeaderInfo = {
    "agreement": "",
    "start_date": "",
    "end_date": "",
    "status": "ACTIVE",
    "budget": 0,
    "overdraft_budget": 0,
    "total_budget": 0,
    "consumed": 0,
    "cart": 0,
    "available": 0,
    "overdraft_consumed": 0,
    "overdraft_cart": 0,
    "overdraft_available": 0,
    "fsa_id": "",
    "currency_code": "USD",
    "currency_locale": "en-US",
    "legal_disclosure": "",
    "mininum_duration": 30
};
function FSADetailedView(props) {
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);
    const { dispatch } = useSupportContext();
    const localization = useLocalization();
    const history = useHistory();

    const groupBy = [
        {
            label: localization.toLanguageString(groupByHostIdKey, mainMessages[siteLanguageDefault][groupByHostIdKey]),
            value: "host",
        },
        {
            label: localization.toLanguageString(groupByProductsKey, mainMessages[siteLanguageDefault][groupByProductsKey]),
            value: "product",
        },
    ];

    const [fsaID, setFsaID] = useState("");
    const [headerInfo, setHeaderInfo] = useState(initialHeaderInfo);
    const [fsaDetails, setFsaDetails] = useState({});

    // sub pool
    const [alreadyOverDraft, setAlreadyOverDraft] = useState(false);
    const [showOverdraft, setShowOverdraft] = useState(false);
    const [selectOverdraft, setSelectOverdraft] = useState(false);

    const [isExpired, setIsExpired] = useState(true);
    const [selectedGroupBy, setSelectedGroupBy] = useState(groupBy[0].value);

    const [licenses, setLicenses] = useState([]);
    const [groupByHosts, setGroupByHosts] = useState([]);
    const [groupByproducts, setGroupByProducts] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [showEmailSuccess, setShowEmailSuccess] = useState(false);
    const [showGenericError, setShowGenericError] = useState(false);

    // handle on group by change
    const onGroupByChange = (e) => {
        setSelectedGroupBy(e.value);
    }

    // handle on expired change
    const onExpiredChange = (e) => {
        setIsExpired(e.target.value);
    }

    /*
     * getFSADetails() gets FSA details
     * @param {id} the string fsa id
    */
    const getFSADetails = (id) => {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        }

        setIsLoading(true);

        axios.get(
            config.fsa_license_view.FSA_DETAILS,
            {
                headers: headers,
                params: {
                    id: id
                }
            }
        )
            .then((response) => {
                if (response.status === 200) {
                    let data = response.data
                    let newHeaderInfo = data['header_info'];
                    newHeaderInfo.start_date = convertDateFormat(newHeaderInfo.start_date);
                    newHeaderInfo.end_date = convertDateFormat(newHeaderInfo.end_date);

                    setHeaderInfo(newHeaderInfo);
                    setFsaDetails(data['header_info']);
                    setLicenses(data['licenses']);

                    //set overdraft flag check
                    setShowOverdraft(newHeaderInfo.overdraft_consumed > 0 || newHeaderInfo.overdraft_cart > 0);
                    setAlreadyOverDraft(newHeaderInfo.overdraft_consumed > 0 || newHeaderInfo.overdraft_cart > 0);
                }
            })
            .catch((error) => {
                console.log('Error: Failed to GET FSA Details', error);
                setShowGenericError(true);
                history.push('/error');
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    // initial get FSA details
    useEffect(() => {
        const id = new URLSearchParams(window.location.search).get('fsa_id');
        setFsaID(id);
        getFSADetails(id);
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const addName = (product) => {
        let name = localization.toLanguageString(hostKey, mainMessages[siteLanguageDefault][hostKey]) + ' ' + product.host_id;
        let nameExcel = product.host_id;
        let icons = {
            checked: true,
            download: true,
            banked: false,
            cloud: false,
            special: false,
        };

        // special handling
        if (product.alias !== null && product.alias !== '') {
            name += ' "' + product.alias + '"'
        }

        // banked transportable
        if (product.status.toLowerCase() === 'banked') {
            name = localization.toLanguageString(transportableLicensesSavedKey, mainMessages[siteLanguageDefault][transportableLicensesSavedKey])
            nameExcel = name
            icons.banked = true
            icons.download = false
        }
        // special handling
        else if (product.host_id === '' || product.host_id === null) {
            name = localization.toLanguageString(hostPendingSpecialKey, mainMessages[siteLanguageDefault][hostPendingSpecialKey])
            nameExcel = name
            icons.special = true
            icons.download = false
        }

        product.name = name
        product.nameExcel = nameExcel
        product.icons = icons

        return product
    }

    // group by hosts/products for licenses
    useEffect(() => {
        let newGroupByHosts = [];
        let newGroupByProducts = [];

        let filteredLicenses = [...licenses];

        if (!isExpired) {
            filteredLicenses = licenses.filter(license => license.status.toLowerCase() !== 'expired');
        }

        filteredLicenses.forEach(license => {
            license = addName(license);
            license.currency_locale = fsaDetails.locale;
            license.currency_code = fsaDetails.currency_code;
            let name = license.name;
            let icons = license.icons;

            let hostIndex = newGroupByHosts.findIndex(host => host.name === name);
            if (hostIndex === -1) {
                newGroupByHosts.push({
                    name: name,
                    host_id: license.host_id,
                    products: [license],
                    icons: icons,
                    expanded: ['.0'],
                });
            } else {
                newGroupByHosts[hostIndex].products.push(license);
            }

            let productIndex = newGroupByProducts.findIndex(product => product.product_number === license.product_number);
            if (productIndex === -1) {
                newGroupByProducts.push({
                    product_number: license.product_number,
                    description: license.description,
                    products: [license],
                });
            } else {
                let host = newGroupByProducts[productIndex].products.findIndex(product => product.name === name);
                if (host === -1) {
                    newGroupByProducts[productIndex].products.push(license);
                }
            }
        })

        newGroupByHosts.forEach(host => {
            let canNotDownload = true;
            let cloud = false;
            let hostTotal = 0;
            let assignedProducts = 0;
            host.products.forEach(product => {
                if (product.status.toLowerCase() === 'redeemed') {
                    assignedProducts += 1;
                    hostTotal += Number(product.total_cost);
                }
                if (product.candownload === 'YES') {
                    canNotDownload = false;
                } else if (product.candownload === 'CLOUD') {
                    canNotDownload = true;
                    cloud = true;
                }
            })

            if (fsaDetails.status.toLowerCase() === 'expired') {
                canNotDownload = true;
            }

            host.assigned_products = assignedProducts;
            host.host_total = formatCurrency(hostTotal, fsaDetails.locale, fsaDetails.currency_code);
            host.icons.download = !canNotDownload;
            host.icons.cloud = cloud;
            host.products = orderBy(host.products, [{
                field: "product_number",
                dir: "asc"
            }]);
        })

        newGroupByProducts.forEach(products => {
            let assignedProducts = 0;
            products.products.forEach(product => {
                if (product.status.toLowerCase() === 'redeemed') {
                    assignedProducts += 1;
                }
            });
            products.assigned_products = assignedProducts;
            products.products = orderBy(products.products, [{
                field: "name",
                dir: "asc"
            }]);
        })

        setGroupByHosts(orderBy(newGroupByHosts, [{field: 'name', dir: 'asc'}]))
        setGroupByProducts(orderBy(newGroupByProducts, [{field: 'product_number', dir: 'asc'}]))
    }, [licenses, isExpired]); // eslint-disable-line react-hooks/exhaustive-deps

    // show overdraft
    useEffect(() => {
        if(headerInfo.overdraft_available < headerInfo.overdraft_budget) {
            setAlreadyOverDraft(true); //ignore_overdraft = 'Y'
            setShowOverdraft(true); //show overdraft donut and button
            setSelectOverdraft(true); //select overdraft donut and button
        } else {
            setAlreadyOverDraft(false);
            setShowOverdraft(false);
            setSelectOverdraft(false);
        }
    }, [headerInfo]);

    const rowRender = (trElement, props) => {
        const expired = props.dataItem.status.toLowerCase() === 'expired' || props.dataItem.status.toLowerCase() === 'disabled' || props.dataItem.status.toLowerCase() === 'pending'
        const expiredStyle = {
            color: "#AAAAAA",
        };
        const trProps = {
            style: expired ? expiredStyle : {},
        };
        return React.cloneElement(
            trElement,
            {...trProps},
            trElement.props.children
        );
    };

    // Support Context Metadata
    // Set Module and Step values on page load
    useEffect(() => {
        const id = new URLSearchParams(window.location.search).get('fsa_id');
        dispatch({ type: 'MODULE_LOAD', payload: {module: 'FSA Detailed View', step: ''}});
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {fsa_id: id}});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <GeneralContext.Provider
                value={{
                    alreadyOverDraft,
                    headerInfo,
                    setHeaderInfo
                }}
            >
                {isLoading ? <Spinner/> : <></>}
                <PageHeader/>
                <div className={"ksm-page-container"}>

                    <div className={"k-h3"}>
                        {localization.toLanguageString(flexibleSpendingAccountsKey, mainMessages[siteLanguageDefault][flexibleSpendingAccountsKey])}
                    </div>
                    <div className={'k-h5'}>
                        {localization.toLanguageString(fsaLicenseViewKey, mainMessages[siteLanguageDefault][fsaLicenseViewKey])}
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <DetailedViewInfo fsaDetails={fsaDetails}/>
                        <SummaryChart
                            showOverdraft={showOverdraft}
                            selectOverdraft={selectOverdraft}
                            setSelectOverdraft={setSelectOverdraft}
                        />
                    </div>


                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '1rem',
                        }}
                    >
                        <div>
                            <RadioGroup
                                data={groupBy}
                                layout={'horizontal'}
                                value={selectedGroupBy}
                                onChange={onGroupByChange}
                            />
                        </div>
                        <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'center'
                        }}>
                            <Label editorId={'fsaDetailExpired'}>
                                {localization.toLanguageString(showExpiredLicensesKey, mainMessages[siteLanguageDefault][showExpiredLicensesKey])}
                            </Label>
                            <Switch
                                id='fsaDetailExpired'
                                onChange={onExpiredChange}
                                checked={isExpired}
                                onLabel={"Yes"}
                                offLabel={"No"}
                            />
                        </div>
                    </div>

                    {showGenericError && (
                        <div style={{width: '33rem'}}>
                            <Alert
                                type={'error'}
                                title={localization.toLanguageString(genericErrorTitleKey, mainMessages[siteLanguageDefault][genericErrorTitleKey])}
                                message={localization.toLanguageString(contactUsHeaderKey, mainMessages[siteLanguageDefault][contactUsHeaderKey])}
                            />
                        </div>
                    )}

                    {selectedGroupBy === 'host' && !showGenericError && (
                        <HostPanelBars
                            fsaID={fsaID}
                            fsaDetails={fsaDetails}
                            groupByHosts={groupByHosts}
                            setGroupByHosts={setGroupByHosts}
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            showEmailSuccess={showEmailSuccess}
                            setShowEmailSuccess={setShowEmailSuccess}
                            rowRender={rowRender}
                        />
                    )}

                    {selectedGroupBy === 'product' && !showGenericError && (
                        <ProductPanelBars
                            fsaID={fsaID}
                            fsaStatus={fsaDetails.status}
                            groupByProducts={groupByproducts}
                            isLoading={isLoading}
                            rowRender={rowRender}
                        />
                    )}
                </div>
            </GeneralContext.Provider>
        </>
    );
}

export default FSADetailedView;

